/**
 * Overlays: drawer, modal, menu, toasts.
 *
 * All of them trap focus, restore it on close, close on Escape, and render
 * their scrim as a real element so a click outside is unambiguous. The product
 * being replaced used a ~200px unlabelled popup with two same-sized buttons for
 * moving money; these are deliberately roomy and explicitly titled.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { Button, IconButton, Spinner, cx } from './core'
import { Icon, type IconName } from './Icon'

/* ------------------------------------------------------------ focus trap -- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface DismissOptions {
  /** Focus the first control on open. Off for a palette, which focuses its input itself. */
  autoFocus?: boolean
  /** Cycle Tab inside the container. Off where the consumer owns Tab (see CommandDialog). */
  trapTab?: boolean
  /** Freeze the page behind the overlay. Off for a popover, which is anchored to a live page. */
  lockScroll?: boolean
  /**
   * Off where Escape has to mean something else first. This hook listens in the
   * CAPTURE phase, so it would close the overlay before any React handler ever
   * ran — a palette whose first Escape clears the query cannot be built on top
   * of it otherwise.
   */
  closeOnEscape?: boolean
  /** Close on a mousedown outside the container — a popover has no scrim to click. */
  closeOnOutside?: boolean
  /**
   * Counts as "inside" for the outside-click test. Without it a click on the
   * trigger closes the panel and the trigger's own onClick immediately reopens
   * it, so the control appears not to toggle.
   */
  ignoreRef?: RefObject<HTMLElement | null>
  /**
   * Pull focus back inside when it escapes. This is containment WITHOUT owning
   * the Tab key, which is what a combobox palette needs: the consumer must be
   * free to give Tab its own meaning (move between result groups) and a capture-
   * phase trap would have moved focus before their handler ever ran.
   */
  keepFocusInside?: boolean
}

function useDismissable(open: boolean, onClose: () => void, options: DismissOptions = {}) {
  const {
    autoFocus = true,
    trapTab = true,
    lockScroll = true,
    closeOnEscape = true,
    closeOnOutside = false,
    keepFocusInside = false,
    ignoreRef,
  } = options
  const ref = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement | null

    const container = ref.current
    if (autoFocus) {
      // Focus the first control, or the container itself if there is none.
      const first = container?.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? container)?.focus()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation()
        onClose()
        return
      }
      if (!trapTab || event.key !== 'Tab' || !container) return
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) return
      const firstItem = items[0]!
      const lastItem = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!container || container.contains(target)) return
      if (ignoreRef?.current?.contains(target)) return
      onClose()
    }

    const onFocusIn = (event: FocusEvent) => {
      if (!container || container.contains(event.target as Node)) return
      const first = container.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? container).focus()
    }

    document.addEventListener('keydown', onKeyDown, true)
    if (closeOnOutside) document.addEventListener('mousedown', onDown, true)
    if (keepFocusInside) document.addEventListener('focusin', onFocusIn, true)

    const previousOverflow = document.body.style.overflow
    if (lockScroll) document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      if (closeOnOutside) document.removeEventListener('mousedown', onDown, true)
      if (keepFocusInside) document.removeEventListener('focusin', onFocusIn, true)
      if (lockScroll) document.body.style.overflow = previousOverflow
      restoreTo.current?.focus?.()
    }
  }, [open, onClose, autoFocus, trapTab, lockScroll, closeOnEscape, closeOnOutside, keepFocusInside, ignoreRef])

  return ref
}

/* ---------------------------------------------------------------- drawer -- */

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: 'default' | 'wide'
}

export function Drawer({ open, onClose, title, subtitle, children, footer, width = 'default' }: DrawerProps) {
  const ref = useDismissable(open, onClose)
  const titleId = useId()
  if (!open) return null

  return createPortal(
    <>
      <div className="w7-scrim" onClick={onClose} />
      <div
        className="w7-drawer"
        data-width={width === 'wide' ? 'wide' : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
        tabIndex={-1}
      >
        <header className="w7-drawer-head">
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId} className="w7-h3">
              {title}
            </h2>
            {subtitle ? <p className="w7-card-sub">{subtitle}</p> : null}
          </div>
          <IconButton name="close" label="Close" onClick={onClose} />
        </header>
        <div className="w7-drawer-body">{children}</div>
        {footer ? <footer className="w7-drawer-foot">{footer}</footer> : null}
      </div>
    </>,
    document.body,
  )
}

/* ----------------------------------------------------------------- modal -- */

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
  tone?: 'neutral' | 'danger'
  /**
   * `md` (480px) is the compact confirm this component was drawn for. `lg`
   * (760px) exists for a plan you have to READ before money moves — one row per
   * account with a direction, an outstanding, a will-move and a reason does not
   * fit in 480px without wrapping every row onto three lines, which is exactly
   * the unreadable "Are you sure?" the product being replaced shipped.
   */
  size?: 'md' | 'lg'
  /**
   * Disables the header close button. Escape and scrim clicks still route
   * through `onClose`, so a caller running an uninterruptible job answers there
   * (with a toast) rather than by removing the exit.
   */
  closeDisabled?: boolean
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  tone = 'neutral',
  size = 'md',
  closeDisabled,
}: ModalProps) {
  const ref = useDismissable(open, onClose)
  const titleId = useId()
  if (!open) return null

  return createPortal(
    <>
      <div className="w7-scrim" onClick={onClose} />
      <div
        className="w7-modal"
        data-size={size === 'md' ? undefined : size}
        role={tone === 'danger' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
        tabIndex={-1}
      >
        <header className="w7-card-head">
          <div className="w7-card-head-titles">
            <h2 id={titleId} className="w7-h3">
              {title}
            </h2>
            {subtitle ? <p className="w7-card-sub">{subtitle}</p> : null}
          </div>
          <IconButton name="close" label="Close" onClick={onClose} disabled={closeDisabled} />
        </header>
        <div className="w7-card-body">{children}</div>
        {footer ? <footer className="w7-card-foot">{footer}</footer> : null}
      </div>
    </>,
    document.body,
  )
}

/**
 * Destructive confirmation that requires typing the subject's name.
 *
 * Used for anything that cannot be undone from the console. A plain
 * "Are you sure? [OK]" is muscle memory within a week; typing the username is
 * not.
 */
export function ConfirmByName({
  open,
  onClose,
  onConfirm,
  title,
  expected,
  children,
  confirmLabel = 'Confirm',
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: ReactNode
  expected: string
  children?: ReactNode
  confirmLabel?: string
}) {
  const [typed, setTyped] = useState('')
  useEffect(() => {
    if (open) setTyped('')
  }, [open])

  const matches = typed.trim() === expected

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      tone="danger"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!matches} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      <label className="w7-field-label" htmlFor="confirm-by-name" style={{ marginTop: 12 }}>
        Type <strong>{expected}</strong> to confirm
      </label>
      <input
        id="confirm-by-name"
        className="w7-input"
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    </Modal>
  )
}

/* ------------------------------------------------------------------ menu -- */

export interface MenuItem {
  id: string
  label: string
  icon?: IconName
  tone?: 'danger'
  disabled?: boolean
  onSelect: () => void
}

/** A row-actions menu. Anchored to its trigger, closes on select or outside click. */
export function Menu({
  items,
  label = 'Actions',
  triggerIcon = 'more',
  trigger,
  header,
  /** `end` = open below, flush to the right edge (account / last-column menus). */
  align = 'start',
}: {
  items: (MenuItem | 'separator')[]
  label?: string
  triggerIcon?: IconName
  /** Custom trigger (e.g. Avatar). Falls back to IconButton + triggerIcon. */
  trigger?: ReactNode
  /** Optional identity / context block above the items. */
  header?: ReactNode
  align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="w7-menu" data-align={align} data-open={open ? 'true' : undefined}>
      {trigger ? (
        <button
          type="button"
          className="w7-menu-trigger"
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          data-active={open ? 'true' : undefined}
          onClick={() => setOpen((v) => !v)}
        >
          {trigger}
        </button>
      ) : (
        <IconButton
          name={triggerIcon}
          label={label}
          size="sm"
          active={open}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        />
      )}
      {open ? (
        <div className="w7-popover" role="menu" data-align={align}>
          {header ? <div className="w7-menu-header">{header}</div> : null}
          {items.map((item, i) =>
            item === 'separator' ? (
              <div key={`sep-${i}`} className="w7-menu-sep" />
            ) : (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className="w7-menuitem"
                data-tone={item.tone}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false)
                  item.onSelect()
                }}
              >
                {item.icon ? <Icon name={item.icon} size={15} /> : null}
                <span className="w7-menuitem-label">{item.label}</span>
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  )
}

/* --------------------------------------------------------------- popover -- */

export interface PopoverProps {
  open: boolean
  onClose: () => void
  /** The trigger. The panel anchors to it and focus returns to it on close. */
  anchorRef: RefObject<HTMLElement | null>
  /** Accessible name — the panel is a dialog and a dialog must be named. */
  label: string
  /** `end` aligns the panel's right edge with the anchor's. */
  align?: 'start' | 'end'
  width?: number
  children: ReactNode
}

/**
 * An anchored, dismissable, focus-trapped panel with no modal chrome.
 *
 * `Menu` renders `MenuItem[]` and nothing else, so it cannot host a range
 * picker's preset list plus two date inputs, a member picker's search box plus
 * checkbox list, or a scope picker's role chips plus tree. Building that shell
 * three times inside three features is how a design system rots — this exposes
 * the behaviour `useDismissable` already had, without the scrim.
 *
 * Portalled and fixed-positioned rather than absolutely positioned inside the
 * trigger: every one of those triggers lives in a toolbar or a table cell with
 * `overflow: auto`, which would clip an in-flow panel.
 */
export function Popover({ open, onClose, anchorRef, label, align = 'start', width = 320, children }: PopoverProps) {
  const ref = useDismissable(open, onClose, {
    lockScroll: false,
    closeOnOutside: true,
    ignoreRef: anchorRef,
  })
  const [box, setBox] = useState<{ top: number; left: number; flipped: boolean } | null>(null)

  // Position after layout but before paint, so the panel never appears at 0,0
  // for a frame. Re-measured on scroll and resize because the anchor moves with
  // the page — a popover left behind by a scroll reads as a rendering fault.
  useLayoutEffect(() => {
    // Drop the last placement on close, so a reopen after the trigger has moved
    // (a toolbar that wrapped, a table that scrolled) never flashes the old spot.
    if (!open) {
      setBox(null)
      return
    }
    const place = () => {
      const anchor = anchorRef.current
      const panel = ref.current
      if (!anchor) return
      const a = anchor.getBoundingClientRect()
      const height = panel?.offsetHeight ?? 0
      const gap = 6
      const below = a.bottom + gap
      const flipped = height > 0 && below + height > window.innerHeight && a.top - gap - height > 0
      const left = align === 'end' ? a.right - width : a.left
      setBox({
        top: flipped ? a.top - gap - height : below,
        left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
        flipped,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, align, width, anchorRef, ref])

  if (!open) return null

  // First paint is placed from the anchor directly rather than parked
  // off-screen: the layout effect above cannot run until the panel exists, and
  // a panel that exists at -9999 for one frame is a panel the browser may
  // scroll to when focus lands in it. Height is unknown here, so this pass
  // always opens downward and the effect corrects the flip before paint.
  const rect = anchorRef.current?.getBoundingClientRect()
  const placed = box ?? {
    top: (rect?.bottom ?? 0) + 6,
    left: Math.max(
      8,
      Math.min(align === 'end' ? (rect?.right ?? width) - width : (rect?.left ?? 0), window.innerWidth - width - 8),
    ),
    flipped: false,
  }

  return createPortal(
    <div
      className="w7-popover-panel"
      role="dialog"
      aria-label={label}
      ref={ref}
      tabIndex={-1}
      data-flipped={placed.flipped ? 'true' : undefined}
      style={{ top: placed.top, left: placed.left, width }}
    >
      {children}
    </div>,
    document.body,
  )
}

/* -------------------------------------------------------- command dialog -- */

export interface CommandDialogProps {
  open: boolean
  onClose: () => void
  /** Accessible name of the dialog. */
  label: string
  value: string
  onValueChange: (next: string) => void
  placeholder: string
  /** Renders a spinner in the input's trailing slot. Never blocks typing. */
  busy?: boolean
  /** DOM id of the highlighted option — the virtual cursor. */
  activeId?: string
  /** DOM id the consumer puts on its listbox. */
  listId: string
  /** The keyboard legend. */
  footer?: ReactNode
  /**
   * The consumer owns every key that moves the cursor. The dialog deliberately
   * does not interpret ↑↓/⏎/Tab itself: what they mean depends on the result
   * list, which the dialog cannot see.
   */
  onInputKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void
  /** sr-only live region under the input, e.g. "41 results". */
  status?: ReactNode
  children: ReactNode
}

/**
 * The shell of a command palette: top-anchored, scrimmed, with a permanently
 * focused combobox over a scrolling results region.
 *
 * Not a `Modal` with different padding. A `Modal` is centred, sizes to its
 * content and focuses its first focusable child — all three are wrong here. A
 * palette must sit high so results grow downward without the box jumping, must
 * keep DOM focus in the input forever so ↑↓ can move a virtual cursor while the
 * operator keeps typing, and must never move focus onto a row. Forking Modal
 * inside a feature would have duplicated the focus-restore and scroll-lock this
 * file already owns.
 */
export function CommandDialog({
  open,
  onClose,
  label,
  value,
  onValueChange,
  placeholder,
  busy,
  activeId,
  listId,
  footer,
  onInputKeyDown,
  status,
  children,
}: CommandDialogProps) {
  const ref = useDismissable(open, onClose, {
    autoFocus: false,
    trapTab: false,
    closeOnEscape: false,
    keepFocusInside: true,
  })
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  if (!open) return null

  return createPortal(
    <>
      <div className="w7-scrim" onClick={onClose} />
      <div
        className="w7-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        ref={ref}
        tabIndex={-1}
        // Escape is handled here, on the bubble, rather than by useDismissable's
        // capture listener: the consumer gets first refusal (their first Escape
        // may mean "clear what I typed") and we close only if they declined it.
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || event.defaultPrevented) return
          event.stopPropagation()
          onClose()
        }}
      >
        <div className="w7-cmdk-input">
          <Icon name="search" size={17} />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label={label}
            autoComplete="off"
            spellCheck={false}
            value={value}
            placeholder={placeholder}
            onChange={(event) => onValueChange(event.target.value)}
            onKeyDown={onInputKeyDown}
          />
          {busy ? <Spinner size={15} /> : null}
        </div>
        <span className="w7-sr" role="status" aria-live="polite">
          {status}
        </span>
        <div className="w7-cmdk-list">{children}</div>
        {footer ? <div className="w7-cmdk-foot">{footer}</div> : null}
      </div>
    </>,
    document.body,
  )
}

/* ---------------------------------------------------------------- toasts -- */

export interface Toast {
  id: number
  tone: 'success' | 'error' | 'warn' | 'info'
  message: ReactNode
  detail?: ReactNode
}

interface ToastApi {
  push(toast: Omit<Toast, 'id'>): void
  success(message: ReactNode, detail?: ReactNode): void
  error(message: ReactNode, detail?: ReactNode): void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const seq = useRef(0)

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = ++seq.current
      setToasts((current) => [...current, { ...toast, id }])
      // Errors stay until dismissed — a failed money movement must not vanish.
      if (toast.tone !== 'error') window.setTimeout(() => remove(id), 5000)
    },
    [remove],
  )

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (message, detail) => push({ tone: 'success', message, detail }),
      error: (message, detail) => push({ tone: 'error', message, detail }),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="w7-toasts" role="region" aria-label="Notifications">
        {toasts.map((toast) => (
          <div key={toast.id} className="w7-toast" data-tone={toast.tone} role="status">
            <Icon
              name={
                toast.tone === 'success'
                  ? 'check'
                  : toast.tone === 'error'
                    ? 'error'
                    : toast.tone === 'warn'
                      ? 'warning'
                      : 'info'
              }
              size={16}
            />
            <div style={{ minWidth: 0 }}>
              <strong>{toast.message}</strong>
              {toast.detail ? <div className="w7-dim">{toast.detail}</div> : null}
            </div>
            <button
              type="button"
              onClick={() => remove(toast.id)}
              aria-label="Dismiss"
              className={cx('w7-iconbtn')}
              style={{ marginLeft: 'auto' }}
            >
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside <ToastProvider>')
  return api
}
